import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '../internal/cx';
import { Spinner } from '../Spinner/Spinner';
import { Tooltip } from '../Tooltip/Tooltip';
import styles from './IconButton.module.css';

export type IconButtonVariant = 'ghost' | 'secondary' | 'primary' | 'danger' | 'nav';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'children'
> {
  /** Accessible name (required: the button has no visible text). */
  label: string;
  icon: ReactNode;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Show the label as a tooltip on hover/focus. Default true. Pass a node for custom text. */
  tooltip?: boolean | ReactNode;
  tooltipPlacement?: 'top' | 'bottom' | 'right' | 'left';
  loading?: boolean;
  /** Small visual badge (e.g. unread count). Decorative: include the info in `label`. */
  badge?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function IconButton({
  label,
  icon,
  variant = 'ghost',
  size = 'md',
  tooltip = true,
  tooltipPlacement = 'bottom',
  loading = false,
  badge,
  type = 'button',
  disabled,
  className,
  onClick,
  ref,
  ...rest
}: IconButtonProps) {
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      disabled={disabled}
      className={cx(styles.button, styles[variant], styles[size], className)}
      onClick={(event) => {
        if (loading) return;
        onClick?.(event);
      }}
      {...rest}
    >
      {loading ? (
        <Spinner size="sm" decorative />
      ) : (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      )}
      {badge != null && badge !== false && (
        <span className={styles.badge} aria-hidden="true">
          {badge}
        </span>
      )}
    </button>
  );

  if (tooltip === false || disabled) return button;
  const content = tooltip === true ? label : tooltip;
  return (
    <Tooltip content={content} placement={tooltipPlacement} describeChild={tooltip !== true}>
      {button}
    </Tooltip>
  );
}
